import type { ScreenSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';

/** Медиа-фичи с перечислимыми значениями: ищем первое совпадение. */
const ENUM_FEATURES: Record<string, string[]> = {
  'prefers-color-scheme': ['light', 'dark'],
  'prefers-reduced-motion': ['no-preference', 'reduce'],
  'prefers-reduced-transparency': ['no-preference', 'reduce'],
  'prefers-contrast': ['no-preference', 'more', 'less', 'custom'],
  'forced-colors': ['none', 'active'],
  'inverted-colors': ['none', 'inverted'],
  pointer: ['none', 'coarse', 'fine'],
  'any-pointer': ['none', 'coarse', 'fine'],
  hover: ['none', 'hover'],
  'any-hover': ['none', 'hover'],
  'color-gamut': ['srgb', 'p3', 'rec2020'],
  'dynamic-range': ['standard', 'high'],
  'display-mode': ['browser', 'standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'],
  scripting: ['none', 'initial-only', 'enabled'],
  update: ['none', 'slow', 'fast'],
  'overflow-block': ['none', 'scroll', 'paged'],
  'overflow-inline': ['none', 'scroll'],
};

/** Числовые медиа-фичи: подбираем значение перебором. */
const NUMERIC_FEATURES: Record<string, number[]> = {
  monochrome: [0, 1, 2, 4, 8],
  color: [0, 1, 2, 4, 8, 10, 12, 16],
};

function collectMediaFeatures(): Record<string, string | boolean> | null {
  return attempt(() => {
    if (typeof matchMedia !== 'function') return null;
    const out: Record<string, string | boolean> = {};

    for (const [feature, values] of Object.entries(ENUM_FEATURES)) {
      for (const value of values) {
        if (matchMedia(`(${feature}: ${value})`).matches) {
          out[feature] = value;
          break;
        }
      }
      // Отсутствие совпадений тоже информативно: фича не поддерживается.
      if (!(feature in out)) out[feature] = false;
    }

    for (const [feature, values] of Object.entries(NUMERIC_FEATURES)) {
      for (const value of values) {
        if (matchMedia(`(${feature}: ${value})`).matches) {
          out[feature] = String(value);
          break;
        }
      }
      if (!(feature in out)) out[feature] = false;
    }

    return out;
  });
}

export const screenModule = defineModule({
  id: 'screen',
  phase: 0,
  timeoutMs: 2000,
  run(): ScreenSignals {
    const s = screen as Screen & Record<string, unknown>;

    return {
      width: attempt(() => s.width),
      height: attempt(() => s.height),
      availWidth: attempt(() => s.availWidth),
      availHeight: attempt(() => s.availHeight),
      // availLeft/availTop есть не во всех движках — это само по себе маркер.
      availLeft: attempt(() => s['availLeft'] as number),
      availTop: attempt(() => s['availTop'] as number),
      colorDepth: attempt(() => s.colorDepth),
      pixelDepth: attempt(() => s.pixelDepth),
      devicePixelRatio: attempt(() => devicePixelRatio),
      isExtended: attempt(() => s['isExtended'] as boolean),
      orientationType: attempt(() => s.orientation?.type ?? null),
      orientationAngle: attempt(() => s.orientation?.angle ?? null),
      innerWidth: attempt(() => innerWidth),
      innerHeight: attempt(() => innerHeight),
      outerWidth: attempt(() => outerWidth),
      outerHeight: attempt(() => outerHeight),
      screenX: attempt(() => screenX),
      screenY: attempt(() => screenY),
      visualViewportScale: attempt(() => visualViewport?.scale ?? null),
      mediaFeatures: collectMediaFeatures(),
    };
  },
});
